/**
 * Pure selection logic behind the Recovery Loop drawer (#279): which
 * eligibility check the "View check" link opens, and which card photo the
 * Extraction pane shows for ONE case on a multi-photo thread.
 *
 * Shared (frontend + functions e2e) so the emulator suite can assert the
 * drawer's actual link target against sweep-produced case docs — the ticket's
 * regression: after a successful re-run the drawer must open the check the
 * recovery PRODUCED, never silently the failure it replaced.
 */

export type RecoveryCheckTargetKind = "original" | "rerun_pending" | "rerun_settled";

export interface RecoveryCheckTarget {
  /** The check the drawer's primary "View check" link opens. */
  checkId: string;
  kind: RecoveryCheckTargetKind;
}

/**
 * Which check the drawer's check link opens, keyed to the recovery ATTEMPT:
 *  1. the fired re-run check, once one exists — still-pending (`check_rerun`)
 *     is labeled in-progress, anything else is the settled result (success or
 *     the re-run's own failure — either way the newest truth of this recovery);
 *  2. the originating failed check only while no re-run has fired (including a
 *     follow-up round, which clears the linkage until its own re-fire stamps
 *     a new one, and `extracted_same`, where no new check ever ran).
 */
export function recoveryCheckTarget(rc: {
  checkId: string;
  rerunCheckId: string | null;
  displayState: string;
}): RecoveryCheckTarget {
  if (!rc.rerunCheckId) return {checkId: rc.checkId, kind: "original"};
  return {
    checkId: rc.rerunCheckId,
    kind: rc.displayState === "check_rerun" ? "rerun_pending" : "rerun_settled",
  };
}

/** The slice of a thread message the photo pick needs. */
export interface CaseCardMessage {
  direction: "inbound" | "outbound";
  /** The case this inbound was attributed to (extraction ladder / agent turn);
   *  null while pending, parked, or on pre-attribution legacy docs. */
  attributedCaseId: string | null;
  media: Array<{intakeId: string; objectIndex: number}>;
}

/**
 * Ordered photo candidates (newest first) for one CASE's Extraction pane.
 * Case-scoped, not thread-scoped: photos attributed to THIS case win; only when
 * the case has none does it fall back to unattributed photos (still pending
 * attribution, or legacy pre-attribution messages) — so a newer photo already
 * attributed to a DIFFERENT case never silently replaces an older case's image.
 * `messages` in thread order (oldest first), as the drawer subscribes them.
 */
export function pickCaseCardMedia(
  messages: CaseCardMessage[],
  caseId: string,
): Array<{intakeId: string; objectIndex: number}> {
  const attributed: Array<{intakeId: string; objectIndex: number}> = [];
  const unattributed: Array<{intakeId: string; objectIndex: number}> = [];
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.direction !== "inbound" || m.media.length === 0) continue;
    if (m.attributedCaseId === caseId) attributed.push(...m.media);
    else if (!m.attributedCaseId) unattributed.push(...m.media);
  }
  return attributed.length > 0 ? attributed : unattributed;
}
